// ============================================================================
// HAI Reporting Tool - User Management Component
// Admin UI for managing users, roles, and ward assignments
// ============================================================================

import { useState, useEffect } from 'react';
import {
  getAllUsers,
  getAllFacilities,
  updateUserRole,
  updateUserFacility,
  toggleUserStatus
} from '@/services/admin.service';
import type { User, Facility, UserRole } from '@/types';

const ROLE_LABELS: Record<UserRole, string> = {
  Admin: 'Administrator',
  IPC_Nurse: 'IPC Nurse',
  Ward_Nurse: 'Ward Nurse',
  Officer: 'Officer'
};

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  Admin: 'Full system access',
  IPC_Nurse: 'Manage infection control data',
  Ward_Nurse: 'Enter ward-level data',
  Officer: 'View reports only'
};

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole>('Ward_Nurse');
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, facilitiesData] = await Promise.all([
        getAllUsers(),
        getAllFacilities()
      ]);
      setUsers(usersData);
      setFacilities(facilitiesData.filter(f => f.isActive));
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setSelectedRole(user.role);
    setSelectedFacilityId(user.facilityId);
  };

  const handleSaveChanges = async () => {
    if (!editingUser) return;

    setError(null);
    setSuccess(null);

    try {
      // Update role if changed
      if (selectedRole !== editingUser.role) {
        await updateUserRole(editingUser.id, selectedRole);
      }

      // Update facility if changed
      if (selectedFacilityId !== editingUser.facilityId) {
        await updateUserFacility(editingUser.id, selectedFacilityId);
      }

      setSuccess('User updated successfully');
      setEditingUser(null);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await toggleUserStatus(user.id, !user.isActive);
      setSuccess(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to update user status');
    }
  };

  const getFacilityName = (facilityId: string) => {
    return facilities.find(f => f.id === facilityId)?.name || '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900">Users</h3>
        <p className="text-sm text-gray-600">Manage user roles and facility assignments</p>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="card bg-primary-50 border border-primary-200">
          <h4 className="font-semibold mb-4">Edit User: {editingUser.displayName}</h4>

          <div className="space-y-4">
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Role
              </label>
              <div className="space-y-2">
                {(Object.keys(ROLE_LABELS) as UserRole[]).map((role) => (
                  <label key={role} className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-white">
                    <input
                      type="radio"
                      value={role}
                      checked={selectedRole === role}
                      onChange={(e) => setSelectedRole(e.target.value as UserRole)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-sm">{ROLE_LABELS[role]}</div>
                      <div className="text-xs text-gray-600">{ROLE_DESCRIPTIONS[role]}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Facility Assignment */}
            {selectedRole !== 'Admin' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign Facility
                </label>
                <select
                  value={selectedFacilityId}
                  onChange={(e) => setSelectedFacilityId(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Select a facility</option>
                  {facilities.map((facility) => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button onClick={handleSaveChanges} className="btn btn-primary">
                Save Changes
              </button>
              <button
                onClick={() => {
                  setEditingUser(null);
                  setSelectedFacilityId('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="card">
        {users.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No users found.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Facility
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.displayName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {ROLE_LABELS[user.role]}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.role === 'Admin' ? 'All facilities' : getFacilityName(user.facilityId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.isActive
                            ? 'bg-success-100 text-success-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.lastLoginAt.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-primary-600 hover:text-primary-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleStatus(user)}
                        className="text-warning-600 hover:text-warning-900"
                      >
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
